import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { USER_URL } from '@insite-store360/shared';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ErrorHandlerService } from './error-handler.service';

export interface SsoRoleAssignment {
  roleId: number;
  projectId: number;
  countryIds?: number[];
  regionIds?: number[];
  storeIds?: number[];
}

export interface SsoMobileUserNamePayload {
  userName?: string | null;
  mdaSda?: string | null;
  mdaSdaCountryId?: number | null;
  basicSalary?: string | number | null;
  tiers?: number | null;
  incentiveSchemeTypeId?: number | null;
  promoterCategory?: string | number | null;
  joiningDate?: string | null;
  releasingDate?: string | null;
  userBatchId?: number | null;
}

export interface SsoRegisterPayload {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  gender?: string;
  isApproval?: boolean;
  isTestUser?: boolean;
  hasProjectManagementAccess?: boolean;
  projectManagementRoleId?: number | null;
  roles: SsoRoleAssignment[];
  mobileUserName?: SsoMobileUserNamePayload;
  reportingOfficerIds?: number[];
}

export interface SsoNamedEntity {
  id: number;
  name: string;
}

export interface SsoRegionEntry {
  id: number;
  name: string;
  countries: SsoNamedEntity[];
}

export interface SsoRoleEntry {
  id: number;
  name: string;
  projectId: number;
  pageAccess?: unknown;
  regions?: SsoRegionEntry[];
}

export interface SsoProjectRoleGroup {
  projectId: number;
  projectName: string;
  roles: SsoRoleEntry[];
}

export interface SsoUserInfo {
  id: number;
  email: string;
  fullName: string;
  status: boolean;
  passwordChangeStatus: boolean;
}

export interface SsoMeResult {
  user: SsoUserInfo;
  projectRoles: SsoProjectRoleGroup[];
  allRoleIds: number[];
  allProjectIds: number[];
}

export interface SsoRegisterResult {
  userId: number;
  fullName: string;
  email: string;
  rolesAssigned: number;
}

export interface SsoApiResponse<T> {
  success: boolean;
  statusCode: number;
  message: string;
  data: T;
  timestamp: string;
}

/**
 * User-service wraps every controller return in `{ status, result }`.
 * SSO endpoints already return `{ success, data, message }`, so the
 * wire shape is usually `{ result: { success, data, message } }`.
 */
function unwrapSsoPayload<T>(response: any): SsoApiResponse<T> {
  const envelope = response?.result ?? response;
  if (envelope?.data !== undefined || envelope?.success !== undefined) {
    return envelope as SsoApiResponse<T>;
  }
  return {
    success: true,
    statusCode: 200,
    message: '',
    data: envelope as T,
    timestamp: new Date().toISOString(),
  };
}

@Injectable({
  providedIn: 'root',
})
export class SsoService {
  private readonly SSO_API = `${USER_URL}/sso`;

  constructor(
    private readonly http: HttpClient,
    private readonly errorHandler: ErrorHandlerService
  ) {}

  register(payload: SsoRegisterPayload): Observable<SsoApiResponse<SsoRegisterResult>> {
    return this.http
      .post<any>(`${this.SSO_API}/register`, payload)
      .pipe(
        map((res) => unwrapSsoPayload<SsoRegisterResult>(res)),
        catchError(this.errorHandler.handleError)
      );
  }

  me(): Observable<SsoApiResponse<SsoMeResult>> {
    return this.http
      .get<any>(`${this.SSO_API}/me`)
      .pipe(
        map((res) => unwrapSsoPayload<SsoMeResult>(res)),
        catchError(this.errorHandler.handleError)
      );
  }

  getRoleAccess(roleId: number): Observable<SsoApiResponse<unknown>> {
    return this.http
      .get<any>(`${this.SSO_API}/role-access/${roleId}`)
      .pipe(
        map((res) => unwrapSsoPayload<unknown>(res)),
        catchError(this.errorHandler.handleError)
      );
  }

  refresh(
    refreshToken: string
  ): Observable<SsoApiResponse<{ accessToken: string; refreshToken: string; issuedAt: string }>> {
    return this.http
      .post<any>(`${this.SSO_API}/refresh`, { refreshToken })
      .pipe(
        map((res) =>
          unwrapSsoPayload<{ accessToken: string; refreshToken: string; issuedAt: string }>(res)
        ),
        catchError(this.errorHandler.handleError)
      );
  }
}
